import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { type ProjectMember } from "@/services/project-member.service";
import { useProjectMembers } from "@/hooks/useProjectMembers";
import {
	MarkdownEditor,
	type MarkdownEditorHandle,
} from "@/components/markdown-editor/MarkdownEditor";
import { getInitials, profileColorClass } from "./types";

const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIME_TYPES = [
	"image/jpeg",
	"image/png",
	"image/webp",
	"image/gif",
	"application/pdf",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	"application/msword",
	"application/vnd.ms-excel",
	"text/plain",
	"application/zip",
	"application/x-zip-compressed",
];

function formatBytes(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface Props {
	projectId: string;
	value: string;
	submitting: boolean;
	placeholder?: string;
	submitLabel?: string;
	autoFocus?: boolean;
	files?: File[];
	onFilesChange?: (files: File[]) => void;
	onChange: (value: string, mentionedIds: string[]) => void;
	onSubmit: () => void;
	onCancel?: () => void;
}

interface MentionState {
	active: boolean;
	query: string;
	tokenStart: number;
	cursor: number;
}

const INACTIVE: MentionState = {
	active: false,
	query: "",
	tokenStart: -1,
	cursor: -1,
};

interface CaretCoords {
	top: number;
	left: number;
}

const MIRROR_COPY_PROPS = [
	"boxSizing",
	"width",
	"borderTopWidth",
	"borderRightWidth",
	"borderBottomWidth",
	"borderLeftWidth",
	"paddingTop",
	"paddingRight",
	"paddingBottom",
	"paddingLeft",
	"fontStyle",
	"fontVariant",
	"fontWeight",
	"fontStretch",
	"fontSize",
	"fontFamily",
	"lineHeight",
	"letterSpacing",
	"textTransform",
	"wordSpacing",
	"whiteSpace",
	"wordWrap",
	"overflowWrap",
	"tabSize",
] as const;

function getCaretCoordinates(
	textarea: HTMLTextAreaElement,
	position: number,
): CaretCoords {
	const style = window.getComputedStyle(textarea);
	const mirror = document.createElement("div");
	for (const prop of MIRROR_COPY_PROPS) {
		mirror.style[prop] = style[prop];
	}
	mirror.style.position = "absolute";
	mirror.style.visibility = "hidden";
	mirror.style.whiteSpace = "pre-wrap";
	mirror.style.wordWrap = "break-word";
	mirror.style.overflow = "hidden";
	mirror.style.top = "0";
	mirror.style.left = "-9999px";

	mirror.textContent = textarea.value.slice(0, position);
	const marker = document.createElement("span");
	marker.textContent = textarea.value.slice(position) || ".";
	mirror.appendChild(marker);

	document.body.appendChild(mirror);
	const lineHeight = parseFloat(style.lineHeight) || 18;
	const top = marker.offsetTop - textarea.scrollTop + lineHeight;
	const left = marker.offsetLeft - textarea.scrollLeft;
	document.body.removeChild(mirror);

	return { top, left };
}

function detectMention(text: string, caret: number): MentionState {
	if (caret <= 0) return INACTIVE;
	let i = caret - 1;
	while (i >= 0) {
		const ch = text[i];
		if (ch === "@") {
			if (i > 0 && /\S/.test(text[i - 1])) return INACTIVE;
			const query = text.slice(i + 1, caret);
			if (/\s/.test(query)) return INACTIVE;
			return { active: true, query, tokenStart: i, cursor: caret };
		}
		if (/\s/.test(ch)) return INACTIVE;
		i -= 1;
	}
	return INACTIVE;
}

// When caret sits inside an already-inserted "@Full Name" token, re-open the
// picker so the user can swap the mention without deleting it by hand.
function findMentionTokenAtCaret(
	text: string,
	caret: number,
	members: ProjectMember[],
): MentionState {
	let best: MentionState = INACTIVE;
	let bestLen = 0;

	for (const m of members) {
		const name = m.profiles.full_name?.trim() || m.profiles.email;
		if (!name) continue;
		const token = `@${name}`;
		let from = 0;
		let idx = text.indexOf(token, from);
		while (idx !== -1) {
			const start = idx;
			const end = idx + token.length;
			// caret anywhere within the token (inclusive of both edges)
			if (caret >= start && caret <= end && token.length > bestLen) {
				best = {
					active: true,
					query: name,
					tokenStart: start,
					cursor: end,
				};
				bestLen = token.length;
			}
			from = idx + 1;
			idx = text.indexOf(token, from);
		}
	}
	return best;
}

export function CommentComposer({
	projectId,
	value,
	submitting,
	placeholder = "Add a comment...",
	submitLabel = "Comment",
	autoFocus = false,
	files,
	onFilesChange,
	onChange,
	onSubmit,
	onCancel,
}: Props) {
	const editorRef = useRef<MarkdownEditorHandle>(null);
	const textarea = () => editorRef.current?.textarea() ?? null;
	const fileInputRef = useRef<HTMLInputElement>(null);
	const allowFiles = typeof onFilesChange === "function";
	const selectedFiles = files ?? [];
	const { members } = useProjectMembers(projectId);
	const [mention, setMention] = useState<MentionState>(INACTIVE);
	const [caretCoords, setCaretCoords] = useState<CaretCoords | null>(null);
	const [highlight, setHighlight] = useState(0);
	const mentionedIdsRef = useRef<Set<string>>(new Set());

	function updateMention(text: string, caret: number) {
		let m = detectMention(text, caret);
		if (!m.active) {
			m = findMentionTokenAtCaret(text, caret, members);
		}
		setMention(m);
		const el = textarea();
		if (m.active && el) {
			setCaretCoords(getCaretCoordinates(el, m.tokenStart));
		} else {
			setCaretCoords(null);
		}
	}

	const filtered = useMemo(() => {
		if (!mention.active) return [];
		const q = mention.query.toLowerCase();
		return members
			.filter((m) => {
				const name = (m.profiles.full_name ?? "").toLowerCase();
				const email = m.profiles.email.toLowerCase();
				return q === "" || name.includes(q) || email.includes(q);
			})
			.slice(0, 6);
	}, [members, mention]);

	useEffect(() => {
		setHighlight(0);
	}, [mention.query, mention.active]);

	const emitMentions = useCallback(
		(nextValue: string) => {
			const stillReferenced = new Set<string>();
			for (const id of mentionedIdsRef.current) {
				const member = members.find((m) => m.user_id === id);
				// Members not loaded yet: keep the id rather than drop it on a
				// transient empty list.
				if (!member) {
					stillReferenced.add(id);
					continue;
				}
				const fullName = member.profiles.full_name?.trim();
				const email = member.profiles.email;
				// selectMember inserts `@${full_name || email}`; keep the id if
				// either token is still present in the body.
				if (
					(fullName && nextValue.includes(`@${fullName}`)) ||
					(email && nextValue.includes(`@${email}`))
				) {
					stillReferenced.add(id);
				}
			}
			mentionedIdsRef.current = stillReferenced;
			return Array.from(stillReferenced);
		},
		[members],
	);

	function handleChange(next: string) {
		const caret = textarea()?.selectionStart ?? next.length;
		updateMention(next, caret);
		const ids = emitMentions(next);
		onChange(next, ids);
	}

	// Runs in the capture phase and stops propagation for picker keys so the
	// editor's own Enter (list continuation) and Tab (indent) never see them.
	function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
		if (mention.active && filtered.length > 0) {
			const pickerKey = ["ArrowDown", "ArrowUp", "Enter", "Tab", "Escape"];
			if (pickerKey.includes(e.key)) e.stopPropagation();
			if (e.key === "ArrowDown") {
				e.preventDefault();
				setHighlight((h) => (h + 1) % filtered.length);
				return;
			}
			if (e.key === "ArrowUp") {
				e.preventDefault();
				setHighlight(
					(h) => (h - 1 + filtered.length) % filtered.length,
				);
				return;
			}
			if (e.key === "Enter" || e.key === "Tab") {
				e.preventDefault();
				selectMember(filtered[highlight]);
				return;
			}
			if (e.key === "Escape") {
				e.preventDefault();
				setMention(INACTIVE);
				setCaretCoords(null);
				return;
			}
		}
		if (e.key === "Escape" && onCancel) {
			e.preventDefault();
			onCancel();
		}
	}

	function selectMember(member: ProjectMember) {
		const name = member.profiles.full_name?.trim() || member.profiles.email;
		const before = value.slice(0, mention.tokenStart);
		const after = value.slice(mention.cursor);
		// Avoid a double space when replacing a token already followed by one.
		const insertion = after.startsWith(" ") ? `@${name}` : `@${name} `;
		const next = before + insertion + after;
		mentionedIdsRef.current.add(member.user_id);
		const ids = emitMentions(next);
		onChange(next, ids);
		setMention(INACTIVE);
		setCaretCoords(null);
		queueMicrotask(() => {
			const el = textarea();
			if (!el) return;
			const pos = before.length + insertion.length;
			el.focus();
			el.setSelectionRange(pos, pos);
		});
	}

	function handleFilesPicked(picked: File[]) {
		if (!onFilesChange) return;
		const next: File[] = [...selectedFiles];
		for (const file of picked) {
			if (!ALLOWED_MIME_TYPES.includes(file.type)) {
				toast.error("Unsupported file type", { description: file.name });
				continue;
			}
			if (file.size > MAX_BYTES) {
				toast.error("File must be 10 MB or smaller", {
					description: file.name,
				});
				continue;
			}
			if (
				next.some(
					(f) => f.name === file.name && f.size === file.size,
				)
			) {
				continue;
			}
			next.push(file);
		}
		onFilesChange(next);
	}

	function removeFile(index: number) {
		if (!onFilesChange) return;
		onFilesChange(selectedFiles.filter((_, i) => i !== index));
	}

	const showDropdown = mention.active && filtered.length > 0;
	const canSubmit = value.trim().length > 0 || selectedFiles.length > 0;

	return (
		<div className="relative flex flex-col gap-2">
			<MarkdownEditor
				ref={editorRef}
				variant="floating"
				value={value}
				onChange={handleChange}
				onFiles={allowFiles ? handleFilesPicked : undefined}
				autoFocus={autoFocus}
				placeholder={placeholder}
				textareaProps={{
					onKeyDownCapture: handleKeyDown,
					onSelect: (e) => {
						const caret =
							e.currentTarget.selectionStart ?? value.length;
						updateMention(value, caret);
					},
				}}
			/>
			{showDropdown && (
				<div
					className="absolute z-30 w-72 max-w-full rounded-md border border-border bg-surface shadow-md overflow-hidden"
					style={{
						top: caretCoords?.top ?? "100%",
						left: caretCoords ? Math.max(0, caretCoords.left) : 0,
					}}
				>
					<ul className="max-h-56 overflow-y-auto py-1">
						{filtered.map((m, idx) => {
							const name =
								m.profiles.full_name || m.profiles.email;
							const initials = getInitials(name);
							const color = profileColorClass(m.user_id);
							return (
								<li key={m.user_id}>
									<button
										type="button"
										onMouseDown={(e) => {
											e.preventDefault();
											selectMember(m);
										}}
										onMouseEnter={() => setHighlight(idx)}
										className={cn(
											"flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm",
											idx === highlight
												? "bg-primary/10"
												: "hover:bg-muted-subtle",
										)}
									>
										<Avatar className="h-6 w-6 shrink-0">
											{m.profiles.avatar_url ? (
												<AvatarImage
													src={m.profiles.avatar_url}
												/>
											) : null}
											<AvatarFallback
												className={cn(
													"text-[10px] text-white",
													color,
												)}
											>
												{initials}
											</AvatarFallback>
										</Avatar>
										<div className="min-w-0 flex-1">
											<div className="truncate text-foreground">
												{name}
											</div>
											{m.profiles.full_name && (
												<div className="truncate text-[11px] text-muted">
													{m.profiles.email}
												</div>
											)}
										</div>
									</button>
								</li>
							);
						})}
					</ul>
				</div>
			)}
			{allowFiles && selectedFiles.length > 0 && (
				<ul className="flex flex-wrap gap-2">
					{selectedFiles.map((file, idx) => (
						<li
							key={`${file.name}-${idx}`}
							className="flex items-center gap-1.5 rounded-md border border-border bg-muted-subtle px-2 py-1 text-xs"
						>
							<Paperclip className="h-3 w-3 text-muted" />
							<span className="max-w-[12rem] truncate text-foreground">
								{file.name}
							</span>
							<span className="text-muted">
								{formatBytes(file.size)}
							</span>
							<button
								type="button"
								onClick={() => removeFile(idx)}
								disabled={submitting}
								className="text-muted hover:text-danger disabled:opacity-50"
								aria-label={`Remove ${file.name}`}
							>
								<X className="h-3 w-3" />
							</button>
						</li>
					))}
				</ul>
			)}
			<div className="flex items-center justify-end gap-2">
				{allowFiles && (
					<>
						<input
							ref={fileInputRef}
							type="file"
							multiple
							className="hidden"
							onChange={(e) => {
								handleFilesPicked(Array.from(e.target.files ?? []));
								e.target.value = "";
							}}
						/>
						<Button
							size="sm"
							variant="ghost"
							className="mr-auto"
							onClick={() => fileInputRef.current?.click()}
							disabled={submitting}
						>
							<Paperclip className="h-3.5 w-3.5 mr-1.5" />
							Attach
						</Button>
					</>
				)}
				{onCancel && (
					<Button
						size="sm"
						variant="ghost"
						onClick={onCancel}
						disabled={submitting}
					>
						Cancel
					</Button>
				)}
				<Button
					size="sm"
					onClick={onSubmit}
					disabled={submitting || !canSubmit}
				>
					{submitting && (
						<Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
					)}
					{submitLabel}
				</Button>
			</div>
		</div>
	);
}
